import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { translations, type Lang, type Translations } from './translations'

const STORAGE_KEY = 'fra-lang'

interface LanguageContextValue {
  lang: Lang
  setLang: (lang: Lang) => void
  t: Translations
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

function getInitialLang(): Lang {
  if (typeof window === 'undefined') return 'it'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'it' || stored === 'en' || stored === 'fr' || stored === 'es') {
    return stored
  }
  return 'it'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(getInitialLang)

  useEffect(() => {
    document.documentElement.lang = lang
    window.localStorage.setItem(STORAGE_KEY, lang)
  }, [lang])

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  )
}

export default LanguageProvider

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext)
  // During HMR module invalidation the provider and consumers can briefly
  // hold different context instances — fall back to Italian for one render
  // instead of crashing the whole tree.
  if (!ctx) {
    return { lang: 'it', setLang: () => {}, t: translations.it }
  }
  return ctx
}
